import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";

export const Events = () => {
  const { data: events = [], isLoading } = useQuery({ queryKey: ["events"], queryFn: () => api.get("/events").then((r) => r.data) });
  const [active, setActive] = useState(null);

  return (
    <div className="pt-32 pb-24 relative z-10">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <p className="eyebrow mb-4">Eventi & Formazioni</p>
          <h1 className="font-display text-4xl sm:text-6xl text-white max-w-3xl leading-tight">Gli ultimi due anni, tra palchi e persone.</h1>
          <p className="text-zinc-400 mt-6 max-w-xl">Una selezione dei momenti più intensi dei miei eventi formativi e delle sessioni con aziende e privati.</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 grid md:grid-cols-2 gap-0 bg-[#121212] border border-[#C19A5B]/30 rounded-2xl overflow-hidden">
            <div className="img-zoom h-64 md:h-auto">
              <img src="https://customer-assets-jai6qajn.emergentagent.net/job_premium-coach-16/artifacts/rqalb8fk_658201097_1366008792234568_1511116941266846657_n.jpg" alt="MindSet Acqui Terme" className="w-full h-full object-cover" />
            </div>
            <div className="p-8 md:p-10 flex flex-col justify-center">
              <span className="eyebrow">Prossimo evento</span>
              <h2 className="font-display text-3xl text-white mt-3 mb-3">MindSet — Acqui Terme</h2>
              <p className="text-zinc-400 leading-relaxed mb-2">Due giornate per liberarti dai vecchi modelli e costruirne uno tutto tuo.</p>
              <p className="text-[#C19A5B] font-medium mb-6">11–12 Aprile 2026</p>
              <a href="/contatti" data-testid="upcoming-event-cta" className="inline-flex w-fit items-center gap-2 rounded-full bg-[#C19A5B] text-black font-semibold px-6 py-3 hover:bg-[#D4AF37] transition-colors">
                Richiedi informazioni
              </a>
            </div>
          </div>
        </Reveal>

        {isLoading ? (
          <p className="text-zinc-500 mt-16">Caricamento…</p>
        ) : (
          <div className="mt-14 columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
            {events.map((e, i) => (
              <Reveal key={e.id} delay={(i % 3) * 0.06}>
                <button
                  data-testid={`event-item-${i}`}
                  onClick={() => setActive(e)}
                  className="img-zoom group block w-full text-left rounded-xl overflow-hidden border border-white/10 relative"
                >
                  <img src={e.image_url} alt={e.title} loading="lazy" decoding="async" className="w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent opacity-90" />
                  <div className="absolute bottom-0 p-5">
                    <p className="text-white font-medium text-lg">{e.title}</p>
                    <p className="text-[#C19A5B] text-xs flex items-center gap-1.5 mt-1"><MapPin size={12} /> {e.location} · {e.event_date}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setActive(null)}
            data-testid="event-lightbox"
          >
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="max-w-4xl w-full bg-[#121212] border border-white/10 rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <img src={active.image_url} alt={active.title} className="w-full max-h-[65vh] object-cover" />
              <div className="p-7">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-display text-2xl text-white">{active.title}</h3>
                    <p className="text-[#C19A5B] text-sm mt-1">{active.location} · {active.event_date}</p>
                  </div>
                  <button onClick={() => setActive(null)} className="text-zinc-400 hover:text-white" data-testid="lightbox-close"><X /></button>
                </div>
                {active.description && <p className="text-zinc-300 mt-4 leading-relaxed">{active.description}</p>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Events;
